import React, { useState } from "react";
import { toast } from "sonner";

import { changePassword } from "../api";
import { useT, useLang } from "../i18n";
import safeStorage from "../lib/safeStorage";
import { tx as __tx } from "../lib/tx";

const TOKEN_KEY = "xpnsr_seo_token";

/**
 * Forced password-change form, rendered when the authenticated user
 * has `must_change_password=true` (typically right after an admin
 * reset). The user can't navigate anywhere else until they finish
 * or sign out.
 */
export default function ChangePassword({ user, minPasswordLen = 8, onChanged, onLogout }) {
  const t = useT();
  const { lang, switchLang } = useLang();
  const tx = t.forceChangePassword;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const minLen = minPasswordLen;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!current) {
      toast.error(tx.currentHolder);
      return;
    }
    if (!next || next.length < minLen) {
      toast.error(tx.minHint(minLen));
      return;
    }
    if (next !== confirm) {
      toast.error(tx.mismatch);
      return;
    }
    setSubmitting(true);
    try {
      await changePassword({ current_password: current, new_password: next });
      toast.success(tx.success);
      if (typeof onChanged === "function") {
        await onChanged();
      } else {
        window.location.reload();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || tx.error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = () => {
    safeStorage.removeItem(TOKEN_KEY);
    if (typeof onLogout === "function") {
      onLogout();
    } else {
      window.location.href = "/";
    }
  };

  return (
    <div className="xp-app xp-app-auth">
      <div className="orb orb-1" aria-hidden="true" />
      <div className="orb orb-2" aria-hidden="true" />

      <main className="xp-main" style={{ marginLeft: 0 }}>
        <div className="xp-top">
          <div className="xp-crumb">
            <span>{__tx("xpnsr")}</span>
            <span className="xp-crumb-sep">/</span>
            <b>SEO</b>
            <span className="xp-crumb-sep">/</span>
            <span>{__tx("change-password")}</span>
          </div>
          <div className="xp-lang" role="group" aria-label={__tx("Language")} style={{ marginLeft: "auto" }}>
            {["en", "ru"].map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => switchLang(l)}
                className={`xp-lang-btn ${lang === l ? "on" : ""}`}
                aria-pressed={lang === l}
              >
                {l === "en" ? "EN" : "RU"}
              </button>
            ))}
          </div>
        </div>

        <div className="xp-auth-wrap">
          <div className="xp-auth-card" data-testid="force-change-card" key={lang}>
            <div className="xp-chrome">
              <div className="xp-chrome-dots">
                <i></i>
                <i></i>
                <i></i>
              </div>
              <div className="xp-chrome-path">{__tx("auth.xpnsr.tech/seo/change-password")}</div>
              <div className="xp-chrome-tls">{__tx("TLS 1.3")}</div>
            </div>

            <div className="xp-card-body">
              <h1 className="xp-h1">
                <em>{tx.title}</em>
              </h1>
              <p className="xp-auth-sub">{tx.sub}</p>

              {user?.email && (
                <p
                  className="xp-auth-sub"
                  data-testid="force-change-email"
                  style={{ marginTop: -6 }}
                >
                  {user.email}
                </p>
              )}

              <form
                onSubmit={handleSubmit}
                noValidate
                aria-label={__tx("Change password form")}
              >
                <div className="xp-f">
                  <label htmlFor="fcp-current">{tx.current}</label>
                  <input
                    id="fcp-current"
                    type="password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    placeholder={tx.currentHolder}
                    autoComplete="current-password"
                    data-testid="force-change-current"
                    required
                  />
                </div>

                <div className="xp-f">
                  <label htmlFor="fcp-next">{tx.nextLabel}</label>
                  <input
                    id="fcp-next"
                    type="password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    autoComplete="new-password"
                    data-testid="force-change-next"
                    minLength={minLen}
                    required
                  />
                  <span className="sr-only">{tx.minHint(minLen)}</span>
                </div>

                <div className="xp-f">
                  <label htmlFor="fcp-confirm">{tx.confirmLabel}</label>
                  <input
                    id="fcp-confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    data-testid="force-change-confirm"
                    minLength={minLen}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="xp-btn-main"
                  disabled={submitting || !current || !next || !confirm}
                  data-testid="force-change-submit"
                >
                  {submitting ? tx.submitting : `${tx.submit} →`}
                </button>

                <button
                  type="button"
                  className="xp-forgot"
                  onClick={handleSignOut}
                  data-testid="force-change-signout"
                >
                  {tx.signOut}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
